import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Entity } from '@toggle/toggle';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { DraggableWatchlistRow } from '../draggable-watchlist-row/DraggableWatchlistRow';
import { useTableShadow } from './hooks/useTableShadow';
import * as S from './TableWatchlistManager.styles';
import { getColumns } from './utils';

export interface TableWatchlistManagerProps {
  data: Entity[];
  countEntitiesAdded: number;
  onRowOrderChange: ({
    toIdx,
    fromIdx,
  }: {
    toIdx: number;
    fromIdx: number;
  }) => void;
  removeEntity: (entity: Entity) => void;
}

export const TableWatchlistManager = ({
  data,
  countEntitiesAdded,
  onRowOrderChange,
  removeEntity,
}: TableWatchlistManagerProps) => {
  const { t } = useTranslation(['watchlistManager']);

  const tableRef = useRef<HTMLDivElement>(null);
  const shadowContainerRef = useRef<HTMLDivElement>(null);
  const columns = useMemo(() => getColumns(t), []);

  useTableShadow({ data, tableRef, shadowContainerRef });
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });
  const tableRows = table.getRowModel().rows;

  return (
    <S.ShadowContainer ref={shadowContainerRef}>
      <S.TableWrapper ref={tableRef}>
        <S.StyledTanStackTableRoot>
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <S.StyledTableHeader alignment="left" key={header.id}>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </S.StyledTableHeader>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {!!data.length ? (
              tableRows.map((row, index) => {
                const isNew = index < countEntitiesAdded;
                return (
                  <DraggableWatchlistRow
                    key={row.id}
                    row={row}
                    onDrop={onRowOrderChange}
                    data={data}
                  >
                    {row.getVisibleCells().map(cell => {
                      return (
                        <S.StyledTableCell key={cell.id} $isNew={isNew}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </S.StyledTableCell>
                      );
                    })}
                    <S.StyledButton
                      variant="critical"
                      size="xsmall"
                      iconName="Close"
                      onClick={() => removeEntity(row.original)}
                    />
                  </DraggableWatchlistRow>
                );
              })
            ) : (
              <tr>
                <td />
              </tr>
            )}
          </tbody>
        </S.StyledTanStackTableRoot>
      </S.TableWrapper>
    </S.ShadowContainer>
  );
};
